'use client';

import { useId, useState } from 'react';
import Text, { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import { RichTextBody } from 'components';
import type { CheckoutHintProps } from '../types';
import styles from './CheckoutHint.module.css';

/**
 * Contentful-driven notice above the checkout cards (PayPal Express new customer, merged cart).
 * The H1 of the step stays in `CartStepLayout`, so the hint title is a plain headline.
 */
export function CheckoutHint({ section, isCollapsible = false, showMoreLabel, showLessLabel }: CheckoutHintProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const bodyId = useId();
  const { title, subtitle, richText } = section;
  const isToggleVisible = isCollapsible && !!showMoreLabel && !!showLessLabel;
  // Without toggle labels the customer could never reveal the text, so show it instead of hiding it.
  const isBodyOpen = !isToggleVisible || isExpanded;

  return (
    <div className={styles['hint']} role="status">
      {title ? (
        <Text variant={TEXT_VARIANT.HEADLINE} tag={TEXT_TAG.P}>
          {title}
        </Text>
      ) : null}
      {subtitle ? (
        <Text variant={TEXT_VARIANT.BODY} tag={TEXT_TAG.P}>
          {subtitle}
        </Text>
      ) : null}
      <div id={bodyId} className={styles['body']} data-expanded={isBodyOpen}>
        <div className={styles['bodyInner']}>
          <RichTextBody text={richText} wrapTextTag={TEXT_TAG.DIV} />
        </div>
      </div>
      {isToggleVisible ? (
        <button
          type="button"
          className={styles['toggle']}
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          onClick={() => setIsExpanded(previous => !previous)}
        >
          <Text variant={TEXT_VARIANT.BODY} tag={TEXT_TAG.SPAN}>
            {isExpanded ? showLessLabel : showMoreLabel}
          </Text>
        </button>
      ) : null}
    </div>
  );
}
